import { Link, useLocation } from "react-router-dom";
import { Mail, Phone, MapPin, ChevronDown, Instagram, Facebook, Linkedin } from "lucide-react";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

const Footer = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    quick: false,
    browse: false,
    guides: false,
    platforms: false,
  });
  const isCancellation = location.pathname === "/cancellation-policy";
  const isTerms = location.pathname === "/terms-of-service";
  const isManagerTerms = location.pathname === "/property-manager-agreement";
  const isPrivacy = location.pathname === "/privacy-policy";

  const toggle = (key: string) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const isOpen = (key: string) => !isMobile || openSections[key];

  // Shared focus + hover styles so every link is keyboard- and pointer-consistent.
  const linkBase =
    "inline-flex items-center rounded-sm transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-primary";
  const activeStyles =
    "text-accent font-semibold border-l-2 border-accent pl-2 -ml-2 hover:text-accent/80 hover:border-accent/80";

  const sectionHeading = "font-display text-base font-semibold";
  const listBase =
    "space-y-2 font-body text-sm text-primary-foreground/85 md:block";
  const sectionBorder =
    "border-t border-primary-foreground/10 pt-6 md:border-0 md:pt-0";

  return (
    <footer
      id="contact"
      role="contentinfo"
      aria-label="Site footer"
      className="bg-primary text-primary-foreground py-10 md:py-16"
    >
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-10">
          {/* Quick Links */}
          <nav aria-labelledby="footer-quick-links" className={sectionBorder}>
            <button
              type="button"
              onClick={() => toggle("quick")}
              aria-expanded={isOpen("quick")}
              aria-controls="footer-quick-links-list"
              className="w-full flex items-center justify-between md:pointer-events-none mb-4 text-left"
            >
              <h2 id="footer-quick-links" className={sectionHeading}>
                Quick Links
              </h2>
              <ChevronDown
                aria-hidden="true"
                className={`h-4 w-4 md:hidden transition-transform ${
                  isOpen("quick") ? "rotate-180" : ""
                }`}
              />
            </button>
            <ul
              id="footer-quick-links-list"
              className={`${listBase} ${isOpen("quick") ? "block" : "hidden"}`}
            >
              <li>
                <Link to="/#properties" className={linkBase} aria-label="Browse our properties">
                  Our Properties
                </Link>
              </li>
              <li>
                <Link to="/#about" className={linkBase} aria-label="Learn about Amplify Vacations">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/rewards" aria-label="Learn about Amplify Adventure Club" className={linkBase}>
                  Adventure Club
                </Link>
              </li>
              <li>
                <Link
                  to="/concierge"
                  aria-label="Find direct rates worldwide and avoid Airbnb service fees"
                  className={linkBase}
                >
                  Find Direct Rates Worldwide
                </Link>
              </li>
              <li>
                <Link
                  to="/connect-ai"
                  aria-label="Connect Amplify Vacations to ChatGPT or Claude"
                  className={linkBase}
                >
                  Connect to ChatGPT & Claude
                </Link>
              </li>
              <li>
                <Link
                  to="/ask-ai-about-panama-city-beach-rentals"
                  aria-label="Answers to common questions about Panama City Beach rentals"
                  className={linkBase}
                >
                  Rental Questions Answered
                </Link>
              </li>

              <li>
                <Link to="/owner" aria-label="Owner Portal login" className={linkBase}>
                  Owner Portal
                </Link>
              </li>
              <li>
                <Link to="/partners" aria-label="List your property with Amplify Vacations" className={linkBase}>
                  List Your Property
                </Link>
              </li>
              <li>
                <Link
                  to="/earnings-calculator"
                  aria-label="Compare Amplify, Airbnb, Vrbo and Booking.com fees"
                  className={linkBase}
                >
                  Fee Impact Calculator
                </Link>
              </li>

              <li>
                <Link
                  to="/cancellation-policy"
                  aria-label="Read our cancellation policy"
                  aria-current={isCancellation ? "page" : undefined}
                  className={`${linkBase} ${isCancellation ? activeStyles : ""}`}
                >
                  Cancellation Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms-of-service"
                  aria-label="Read our terms of service"
                  aria-current={isTerms ? "page" : undefined}
                  className={`${linkBase} ${isTerms ? activeStyles : ""}`}
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link
                  to="/property-manager-agreement"
                  aria-label="Read the property manager agreement"
                  aria-current={isManagerTerms ? "page" : undefined}
                  className={`${linkBase} ${isManagerTerms ? activeStyles : ""}`}
                >
                  Property Manager Agreement
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy-policy"
                  aria-label="Read our privacy policy"
                  aria-current={isPrivacy ? "page" : undefined}
                  className={`${linkBase} ${isPrivacy ? activeStyles : ""}`}
                >
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </nav>

          {/* Browse Rentals */}
          <nav aria-labelledby="footer-browse-rentals" className={sectionBorder}>
            <button
              type="button"
              onClick={() => toggle("browse")}
              aria-expanded={isOpen("browse")}
              aria-controls="footer-browse-rentals-list"
              className="w-full flex items-center justify-between md:pointer-events-none mb-4 text-left"
            >
              <h2 id="footer-browse-rentals" className={sectionHeading}>
                Browse Rentals
              </h2>
              <ChevronDown
                aria-hidden="true"
                className={`h-4 w-4 md:hidden transition-transform ${
                  isOpen("browse") ? "rotate-180" : ""
                }`}
              />
            </button>
            <ul
              id="footer-browse-rentals-list"
              className={`${listBase} ${isOpen("browse") ? "block" : "hidden"}`}
            >
              <li>
                <Link to="/pcb-condo-rentals" className={linkBase}>
                  Condo Rentals
                </Link>
              </li>
              <li>
                <Link to="/pcb-family-friendly-rentals" className={linkBase}>
                  Family-Friendly Rentals
                </Link>
              </li>
              <li>
                <Link to="/pcb-beachfront-rentals" className={linkBase}>
                  Beachfront Rentals
                </Link>
              </li>
              <li>
                <Link to="/pcb-pet-friendly-rentals" className={linkBase}>
                  Pet-Friendly Rentals
                </Link>
              </li>
              <li>
                <Link to="/pcb-rentals-with-pool" className={linkBase}>
                  Rentals with a Pool
                </Link>
              </li>
              <li>
                <Link to="/pcb-private-pool-beach-houses" className={linkBase}>
                  Private-Pool Beach Houses
                </Link>
              </li>
              <li>
                <Link to="/pcb-large-group-rentals" className={linkBase}>
                  Large Group Rentals
                </Link>
              </li>
            </ul>
          </nav>

          {/* PCB Guides */}
          <nav aria-labelledby="footer-pcb-guides" className={sectionBorder}>
            <button
              type="button"
              onClick={() => toggle("guides")}
              aria-expanded={isOpen("guides")}
              aria-controls="footer-pcb-guides-list"
              className="w-full flex items-center justify-between md:pointer-events-none mb-4 text-left"
            >
              <h2 id="footer-pcb-guides" className={sectionHeading}>
                PCB Guides
              </h2>
              <ChevronDown
                aria-hidden="true"
                className={`h-4 w-4 md:hidden transition-transform ${
                  isOpen("guides") ? "rotate-180" : ""
                }`}
              />
            </button>
            <ul
              id="footer-pcb-guides-list"
              className={`${listBase} ${isOpen("guides") ? "block" : "hidden"}`}
            >
              <li>
                <Link to="/things-to-do-in-panama-city-beach" className={linkBase}>
                  Things to Do in PCB
                </Link>
              </li>
              <li>
                <Link to="/getting-to-panama-city-beach" className={linkBase}>
                  Getting to PCB
                </Link>
              </li>
              <li>
                <Link to="/panama-city-beach-access-map" className={linkBase}>
                  Beach Access Guide
                </Link>
              </li>
              <li>
                <Link to="/best-time-to-visit-panama-city-beach" className={linkBase}>
                  Best Time to Visit
                </Link>
              </li>
              <li>
                <Link to="/panama-city-beach-spring-break-dates" className={linkBase}>
                  Spring Break Dates
                </Link>
              </li>
              <li>
                <Link to="/panama-city-beach-events-calendar" className={linkBase}>
                  Events Calendar
                </Link>
              </li>
            </ul>
          </nav>

          {/* Other Platform Guides */}
          <nav aria-labelledby="footer-platform-guides" className={sectionBorder}>
            <button
              type="button"
              onClick={() => toggle("platforms")}
              aria-expanded={isOpen("platforms")}
              aria-controls="footer-platform-guides-list"
              className="w-full flex items-center justify-between md:pointer-events-none mb-4 text-left"
            >
              <h2 id="footer-platform-guides" className={sectionHeading}>
                Other Platform Guides
              </h2>
              <ChevronDown
                aria-hidden="true"
                className={`h-4 w-4 md:hidden transition-transform ${
                  isOpen("platforms") ? "rotate-180" : ""
                }`}
              />
            </button>
            <ul
              id="footer-platform-guides-list"
              className={`${listBase} ${isOpen("platforms") ? "block" : "hidden"}`}
            >
              <li>
                <Link
                  to="/booking-fees-explained"
                  aria-label="Read our guides explaining vacation rental booking fees"
                  className={linkBase}
                >
                  Booking Fees Explained
                </Link>
              </li>
              <li>
                <Link
                  to="/airbnb-fees-explained"
                  aria-label="Read our guide explaining Airbnb fees"
                  className={linkBase}
                >
                  Airbnb Fees Explained
                </Link>
              </li>
              <li>
                <Link
                  to="/vrbo-fees-explained"
                  aria-label="Read our guide explaining Vrbo fees"
                  className={linkBase}
                >
                  Vrbo Fees Explained
                </Link>
              </li>
              <li>
                <Link
                  to="/booking-com-fees-explained"
                  aria-label="Read our guide explaining Booking.com fees"
                  className={linkBase}
                >
                  Booking.com Fees Explained
                </Link>
              </li>
            </ul>
          </nav>

          {/* Contact — far right */}
          <div className={sectionBorder}>
            <h2 id="footer-contact" className="font-display text-base font-semibold mb-4">
              Contact
            </h2>
            <ul
              aria-labelledby="footer-contact"
              className="space-y-3 font-body text-sm text-primary-foreground/85 not-italic"
            >
              <li className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-accent shrink-0" aria-hidden="true" />
                <address className="not-italic">Panama City Beach, FL 32413</address>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-accent shrink-0" aria-hidden="true" />
                <a
                  href="mailto:support@amplifyvacations.com"
                  aria-label="Email Amplify Vacations at support@amplifyvacations.com"
                  className={linkBase}
                >
                  support@amplifyvacations.com
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-accent shrink-0" aria-hidden="true" />
                <a
                  href="tel:+19045804085"
                  aria-label="Call Amplify Vacations at (904) 580-4085"
                  className={linkBase}
                >
                  (904) 580-4085
                </a>
              </li>
              <li className="flex items-center gap-4 pt-1">
                {[
                  { href: "https://www.instagram.com/amplifyvacations/", label: "Instagram", icon: <Instagram className="h-5 w-5" aria-hidden="true" /> },
                  { href: "https://www.facebook.com/profile.php?id=61594072793583", label: "Facebook", icon: <Facebook className="h-5 w-5" aria-hidden="true" /> },
                  { href: "https://www.linkedin.com/company/amplify-vacations/", label: "LinkedIn", icon: <Linkedin className="h-5 w-5" aria-hidden="true" /> },
                  {
                    href: "https://www.tiktok.com/@amplifyvacations",
                    label: "TikTok",
                    icon: (
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                        <path d="M19.6 6.7a4.8 4.8 0 0 1-3.8-4.2V2h-3.4v13.6a2.9 2.9 0 1 1-2-2.7V9.4a6.3 6.3 0 1 0 5.4 6.2V8.7a8.2 8.2 0 0 0 3.8 1.2V6.7Z" />
                      </svg>
                    ),
                  },
                ].map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer me"
                    aria-label={`Amplify Vacations on ${s.label}`}
                    className="text-accent hover:text-primary-foreground transition-colors"
                  >
                    {s.icon}
                  </a>
                ))}
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-primary-foreground/10 mt-12 pt-8 text-center">
          <p className="text-primary-foreground/75 font-body text-xs">
            © 2026 Amplify Vacations. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
